import {
  groupBy,
  intersection,
  isEmpty,
  isEqual,
  omit,
  partition,
  round,
  sortBy,
} from 'lodash';
import invariant from 'tiny-invariant';
import { Client } from 'urql';
import { ModuleThread, Pool } from 'threads';
import {
  ColumnType,
  DatabaseLookupType,
  ElementIdentifierDbidType,
  ForgeAttributePredicateType,
  ForgeAttributeQueryType,
  ForgeAttributeType,
  OrderColumnDeepFragment,
  OrderDeepFragment,
  OrderEntryDeepFragment,
  PredicateType,
  Quantity,
  QuantityEnum,
  UnitSettingsType,
} from '../gql/graphql';
import { difference, merge } from '../utils/dbid-utils';
import {
  batchResolveDatabaseLookups,
  createDynamicLookupResolver,
  DatabaseLookupMap,
  DynamicLookupResolver,
  DynamicSearchTermScope,
  evaluateDynamicSearchTerm,
  extractDatabaseLookups,
  getDatabaseLookupKey,
  isDynamicSearchTerm,
  seedDatabaseLookupCache,
} from '../hooks/useDatabaseLookup';

import { getColorValueFromGradient } from '../utils/color-util';
import {
  decodeColorWithPattern,
  encodeColorWithPattern,
} from '../utils/color-pattern-encoding';
import {
  convertQuantityToSIUnits,
  convertSIQuantity,
  formatUnit,
  roundingToPrecision,
  unitToUnitType,
} from '../utils/unit-utils';
import {
  PropertyContext,
  resolveElementIdentifiersDBID,
  resolveProperties,
  resolveQuantity,
} from './property-operations';
import { ResolvedQuantity } from './property-types';
import { resolveDerivedFields } from './resolve-formula';
import {
  SerializeNHashModule,
  bulkComputeHashInWorker,
  BulkHashOutput,
} from './worker/hashing-pool';
import { Point } from './geometry/geometric-types';
import { makeTinierBatches, runWorkInBatches } from 'src/utils/promise-pool';
import { dedupeOperations, dedupedMutation } from 'src/utils/dedupe-mutations';
import { ResolvedOrder } from 'src/hooks/order';
import { parseLabels } from 'src/utils/label-utils';

export type ResolvedCustomField = Omit<
  OrderEntryDeepFragment['customFields'][0],
  'resolvedValue'
> & {
  resolvedValue: ResolvedQuantity;
  isSomeQuantityMissing: boolean;
};

export type ResolvedOrderEntry = Omit<
  OrderEntryDeepFragment,
  'customFields'
> & {
  isMissingResolvedQuantity: boolean;
  subRows?: ResolvedOrderEntry[];
  resolvedElementIdentifiersDbid: ElementIdentifierDbidType[];
  resolvedQuantity: ResolvedQuantity | null;
  customFields: ResolvedCustomField[];
};

export type ResolvedQuantities = {
  resolvedCustomFields: ResolvedCustomField[];
  resolvedQuantity: ResolvedQuantity | null;
  isMissingResolvedQuantity: boolean;
};

type OrderEntriesCache = {
  inputs: Map<string, Partial<OrderEntryDeepFragment>>;
  context: PropertyContext | null;
  resolvedOrders: Map<string, ResolvedOrderEntry>;
  projectId: string;
  projectSettings: {
    maxTrianglesForComplexAlgorithm: number;
    globalOffset: Point;
    unitSettings?: UnitSettingsType;
  };
};

const orderEntriesCache: OrderEntriesCache = {
  inputs: new Map(),
  context: null,
  resolvedOrders: new Map<string, ResolvedOrderEntry>(),
  projectId: '',
  projectSettings: {
    maxTrianglesForComplexAlgorithm: 5000,
    globalOffset: [0, 0, 0],
    unitSettings: undefined,
  },
};

export interface ResolvedOrderReturnType {
  value: ResolvedOrderEntry;
  hash: string | null | undefined;
  persistToBackend: boolean;
}

// eslint-disable-next-line complexity
export const resolveOrderV2 = async (
  pool: Pool<ModuleThread<SerializeNHashModule>> | null,
  projectId: string,
  order: OrderDeepFragment,
  context: PropertyContext,
  backendCacheEnabled: boolean,
  isReadOnly: boolean,
  projectSettings: {
    maxTrianglesForComplexAlgorithm: number;
    globalOffset: Point;
    unitSettings?: UnitSettingsType;
  },
  shouldSetCache = true,
  databaseLookupMap: DatabaseLookupMap = new Map(),
  dynamicLookupResolver?: DynamicLookupResolver
): Promise<{
  resolvedOrder: ResolvedOrder;
  entriesToCacheInBackend: Array<ResolvedOrderEntry>;
  hashesWithEntryIds: Array<BulkHashOutput>;
}> => {
  const hasProjectGlobsChanged = isEqual(
    { projectSettings, context },
    {
      projectSettings: orderEntriesCache.projectSettings,
      context: orderEntriesCache.context,
    }
  );
  if (projectId !== orderEntriesCache.projectId || !hasProjectGlobsChanged) {
    orderEntriesCache.inputs = new Map();
    orderEntriesCache.resolvedOrders = new Map();
    orderEntriesCache.projectId = projectId;
    orderEntriesCache.projectSettings = projectSettings;
    orderEntriesCache.context = context;
  }

  // Omit fields used for caching from the entry at most once to make the comp less expensive
  const sanitizedEntries = order.orderEntries.map((entry) =>
    omit(entry, ['orderEntryCache'])
  );
  // find all the entries that are already in the F.E cache and that are valid and those that aren't
  const [resolvedEntriesInFrontendCache, entriesNotInFrontendCache] =
    getEntriesInFrontendCache(sanitizedEntries, isReadOnly);

  // Apply database lookups to cached entries if we have a lookup map
  // This ensures cached entries get updated with database lookup values
  const cachedEntriesWithLookups =
    databaseLookupMap.size > 0
      ? await applyDatabaseLookupsToCachedEntries(
          resolvedEntriesInFrontendCache,
          order.orderColumns,
          databaseLookupMap
        )
      : resolvedEntriesInFrontendCache;

  // if all entries are in the F.E cache then return early
  if (entriesNotInFrontendCache.length === 0) {
    const resolvedOrder = {
      ...order,
      labels: parseLabels(order.labels) as ResolvedOrder['labels'],
      orderEntries: sortBy(cachedEntriesWithLookups, [
        (entry) => entry.createdAt,
        (entry) => entry.name,
      ]),
    };

    return {
      resolvedOrder,
      entriesToCacheInBackend: [],
      hashesWithEntryIds: [],
    };
  }

  const entriesToCacheInBackend: Array<ResolvedOrderEntry> = [];
  const entriesToCacheInFrontend: Map<string, ResolvedOrderEntry> = new Map();
  let hashesWithEntryIds: Array<BulkHashOutput> = [];

  if (backendCacheEnabled) {
    // We want to skip all these processes if we are in the sharing and just return whatever backend cache that ships with the entries
    if (!isReadOnly) {
      const loadedModels = Object.keys(context.loadedModels);
      const orderColumnIds = order.orderColumns.map((col) => col.id);
      const input = { loadedModels, context, projectSettings, orderColumnIds };
      hashesWithEntryIds = await bulkComputeHashInWorker(
        input,
        entriesNotInFrontendCache,
        pool
      );
    }

    const hashMap = new Map(
      hashesWithEntryIds.map((hash) => [hash.entryId, hash])
    );

    const resolvedEntriesInBackendCache = getEntriesInBackendCache(
      order.orderEntries,
      hashMap,
      isReadOnly
    );
    // combination of b.e + f.e cache consists of all the cached resolved entries
    const allCachedResolvedEntries = [
      ...resolvedEntriesInBackendCache,
      ...cachedEntriesWithLookups, // Use the updated frontend cached entries with lookups applied
    ];

    // Apply database lookups to backend cached entries as well
    const allCachedEntriesWithLookups =
      databaseLookupMap.size > 0
        ? await applyDatabaseLookupsToCachedEntries(
            allCachedResolvedEntries,
            order.orderColumns,
            databaseLookupMap
          )
        : allCachedResolvedEntries;

    const allResolvedEntryIds = new Set<string>(
      allCachedResolvedEntries.map(({ id }) => id)
    );
    const entriesNotInCache = sanitizedEntries.filter(
      (entry) => !allResolvedEntryIds.has(entry.id)
    );

    if (entriesNotInCache.length === 0) {
      // since everything is either in F.E cache or B.E cache,
      // we only need to set what is missing from the F.E cache with B.E cache and return
      if (shouldSetCache) {
        for (const resolvedEntry of resolvedEntriesInBackendCache) {
          const entry = sanitizedEntries.find(
            (oe) => oe.id === resolvedEntry.id
          )!;
          orderEntriesCache.inputs.set(entry.id, entry);
          orderEntriesCache.resolvedOrders.set(resolvedEntry.id, resolvedEntry);
        }
      }

      const resolvedOrder = {
        ...order,
        labels: parseLabels(order.labels) as ResolvedOrder['labels'],
        orderEntries: sortBy(allCachedEntriesWithLookups, [
          (entry) => entry.createdAt,
          (entry) => entry.name,
        ]),
      };

      return { resolvedOrder, entriesToCacheInBackend: [], hashesWithEntryIds };
    }

    // Resolve the entries that are not in any cache
    const operations = entriesNotInCache.map((entry) => {
      const hashWithId = hashMap.get(entry.id);
      const op = () =>
        resolveEntry(
          entry,
          order.orderColumns,
          context,
          projectSettings,
          databaseLookupMap,
          dynamicLookupResolver
        );

      return dedupeOperations(`${entry.id}-${hashWithId?.hash}`, op);
    });

    const dedupedOpResult = await Promise.all(
      operations.map(async ({ original, valuePromise }) => ({
        original,
        value: await valuePromise,
      }))
    );

    const allResolved: Array<ResolvedOrderEntry> = dedupedOpResult.map(
      (rslt) => rslt.value
    );

    // Add both the resolvedEntriesInBackendCache and original resolved into the entriesToCacheInFrontend
    for (const resolvedEntry of [
      ...resolvedEntriesInBackendCache,
      ...allResolved,
    ]) {
      entriesToCacheInFrontend.set(resolvedEntry.id, resolvedEntry);
    }

    // original resolved entries do the actual wait, non-original are just served a pending promise to wait on
    // the reason we want originalResolved is so that we only add single entries for caching in the backend
    // we essentially want to trim the number of requests going to the backend i.e., if it is not original
    // then it means that a fetch request has been sent out for it or has been queued to be sent out
    const originalResolved: Array<ResolvedOrderEntry> = dedupedOpResult
      .filter((rslt) => rslt.original === true)
      .map((rslt) => rslt.value);
    entriesToCacheInBackend.push(...originalResolved);
  } else {
    const resolvePromises = entriesNotInFrontendCache.map((entryToResolve) => {
      return resolveEntry(
        entryToResolve,
        order.orderColumns,
        context,
        projectSettings,
        databaseLookupMap,
        dynamicLookupResolver
      );
    });

    const resolvedEntries = await Promise.all(resolvePromises);
    for (const resolvedEntry of resolvedEntries) {
      entriesToCacheInFrontend.set(resolvedEntry.id, resolvedEntry);
    }
  }

  if (shouldSetCache) {
    for (const resolvedEntry of Object.values(entriesToCacheInFrontend)) {
      const entry = sanitizedEntries.find((oe) => oe.id === resolvedEntry.id)!;
      // Not having an entry should be enforced as an invariant
      orderEntriesCache.inputs.set(entry.id, entry);
      orderEntriesCache.resolvedOrders.set(resolvedEntry.id, resolvedEntry);
    }
  }

  // Use the updated cached entries with lookups applied
  const feCachedEnties: Array<[string, ResolvedOrderEntry]> =
    cachedEntriesWithLookups.map((oe) => [oe.id, oe]);
  const allResolvedEntriesMap = new Map([
    ...feCachedEnties,
    ...entriesToCacheInFrontend.entries(),
  ]);
  const allResolvedEntries = order.orderEntries
    .map((entry) => allResolvedEntriesMap.get(entry.id))
    .filter((item): item is ResolvedOrderEntry => item != undefined);

  const resolvedOrder = {
    ...order,
    labels: parseLabels(order.labels) as ResolvedOrder['labels'],
    orderEntries: sortBy(allResolvedEntries, [
      (entry) => entry.createdAt,
      (entry) => entry.name,
    ]),
  };

  return { resolvedOrder, entriesToCacheInBackend, hashesWithEntryIds };
};

/**
 * Converts a resolved quantity value to a string for caching.
 */
function resolvedValueToString(
  value: string | number | (string | number)[] | undefined | null
): string | null {
  if (value === undefined || value === null || value === '') return null;
  if (typeof value === 'number') return String(value);
  if (Array.isArray(value)) return value[0]?.toString() ?? null;
  return value;
}

/**
 * Checks if two database lookup configs are equal.
 */
function areDatabaseLookupsEqual(
  a: DatabaseLookupType | null | undefined,
  b: DatabaseLookupType | null | undefined
): boolean {
  if (!a || !b) return false;
  return (
    a.databaseId === b.databaseId &&
    a.searchColumnId === b.searchColumnId &&
    a.valueColumnId === b.valueColumnId &&
    a.searchTerm === b.searchTerm &&
    (a.lookupProfileId ?? null) === (b.lookupProfileId ?? null)
  );
}

/**
 * Seeds the cache from an entry's main quantity if it's a database lookup.
 * Only seeds if the current lookup config matches the cached config.
 */
function seedCacheFromMainQuantity(
  entry: OrderEntryDeepFragment,
  cachedEntry: ResolvedOrderEntry
): void {
  if (
    entry.quantity?.type !== QuantityEnum.DatabaseLookup ||
    !entry.quantity.databaseLookup
  ) {
    return;
  }

  // Only seed if the lookup config hasn't changed since caching
  const currentLookup = entry.quantity.databaseLookup;
  const cachedLookup = cachedEntry.quantity?.databaseLookup;
  if (!areDatabaseLookupsEqual(currentLookup, cachedLookup)) {
    return;
  }

  const resolvedValue = cachedEntry.resolvedQuantity?.value;
  const stringValue = resolvedValueToString(resolvedValue);
  if (stringValue === null) return;

  const key = getDatabaseLookupKey(entry.quantity.databaseLookup);
  if (key) {
    seedDatabaseLookupCache(key, {
      value: stringValue,
      found: !cachedEntry.isMissingResolvedQuantity,
    });
  }
}

/**
 * Seeds the cache from an entry's custom fields if they are database lookups.
 */
function seedCacheFromCustomFields(
  entry: OrderEntryDeepFragment,
  cachedEntry: ResolvedOrderEntry
): void {
  for (const cf of entry.customFields ?? []) {
    if (
      cf.value?.type !== QuantityEnum.DatabaseLookup ||
      !cf.value.databaseLookup
    ) {
      continue;
    }

    const cachedCf = cachedEntry.customFields?.find(
      (ccf) => ccf.orderColumnId === cf.orderColumnId
    );

    // Only seed if the lookup config hasn't changed since caching
    const currentLookup = cf.value.databaseLookup;
    const cachedLookup = cachedCf?.value?.databaseLookup;
    if (!areDatabaseLookupsEqual(currentLookup, cachedLookup)) {
      continue;
    }

    const resolvedValue = cachedCf?.resolvedValue?.value;
    const stringValue = resolvedValueToString(resolvedValue);
    if (stringValue === null) continue;

    const key = getDatabaseLookupKey(cf.value.databaseLookup);
    if (key) {
      seedDatabaseLookupCache(key, {
        value: stringValue,
        found: !cachedCf?.isSomeQuantityMissing,
      });
    }
  }
}

/**
 * Seeds the database lookup cache from backend-cached entries.
 * This ensures we don't re-run LLM lookups for entries that were already resolved.
 */
function seedCacheFromBackendEntries(orders: Array<OrderDeepFragment>): void {
  for (const order of orders) {
    for (const entry of order.orderEntries) {
      if (!entry.orderEntryCache?.value) continue;

      const cachedEntry = entry.orderEntryCache.value;
      seedCacheFromMainQuantity(entry, cachedEntry);
      seedCacheFromCustomFields(entry, cachedEntry);
    }
  }
}

export const resolveOrdersV2 = async (
  pool: Pool<ModuleThread<SerializeNHashModule>> | null,
  orders: Array<OrderDeepFragment>,
  projectId: string,
  context: PropertyContext,
  urqlClient: Client,
  backendCacheEnabled: boolean,
  isReadOnly = false,
  projectSettings: {
    maxTrianglesForComplexAlgorithm: number;
    globalOffset: Point;
    unitSettings?: UnitSettingsType;
  }
): Promise<Array<ResolvedOrder>> => {
  // Seed cache from backend-cached entries to avoid re-running LLM lookups
  seedCacheFromBackendEntries(orders);

  // Pre-resolve all static database lookups in batch
  const lookups = extractDatabaseLookups(orders);
  const databaseLookupMap =
    lookups.length > 0
      ? await batchResolveDatabaseLookups(urqlClient, lookups)
      : new Map();

  // Create resolver for dynamic lookups (those with variable references)
  const dynamicLookupResolver = createDynamicLookupResolver(urqlClient);

  const { errors, results } = await runWorkInBatches(orders, async (order) => {
    return resolveOrderV2(
      pool,
      projectId,
      order,
      context,
      backendCacheEnabled,
      isReadOnly,
      projectSettings,
      true, // shouldSetCache
      databaseLookupMap,
      dynamicLookupResolver
    );
  });
  if (errors.length > 0) {
    console.error(errors);
  }

  // only set the backend cache if the backend cache has been enabled and if we are not in a shared state?
  if (backendCacheEnabled && !isReadOnly) {
    const backendCacheEntries = results.flatMap(
      (rst) => rst.entriesToCacheInBackend
    );

    const hashesWithEntryIds = results.flatMap((rst) => rst.hashesWithEntryIds);

    const dataForBackendCache: Array<{
      value: ResolvedOrderEntry;
      hash: string;
      orderEntryId: string;
    }> = [];
    const hashMap = new Map(
      hashesWithEntryIds.map((hash) => [hash.entryId, hash])
    );
    backendCacheEntries.forEach((entry) => {
      const hashWithId = hashMap.get(entry.id);
      invariant(hashWithId, 'Hash should be present for this entry');
      dataForBackendCache.push({
        value: getResolvedEntryForBackendCaching(entry),
        hash: hashWithId.hash,
        orderEntryId: entry.id,
      });
    });

    if (dataForBackendCache.length > 0) {
      const batchedWork = makeTinierBatches(dataForBackendCache);
      await runWorkInBatches(batchedWork, async (work) => {
        const key = work
          .map(({ hash, orderEntryId }) => `${hash}-${orderEntryId}`)
          .sort()
          .join();
        return dedupedMutation(urqlClient, key, work);
      });
    }
  }

  return results.map((rst) => rst.resolvedOrder);
};

/**
 * Applies a database lookup to a custom field if applicable.
 */
function applyLookupToCustomField(
  cf: ResolvedCustomField,
  databaseLookupMap: DatabaseLookupMap,
  entryId: string
): { updated: boolean; field: ResolvedCustomField } {
  if (
    cf.value?.type === QuantityEnum.DatabaseLookup &&
    cf.value.databaseLookup
  ) {
    const lookup = cf.value.databaseLookup;
    if (!isDynamicSearchTerm(lookup.searchTerm)) {
      const key = getDatabaseLookupKey(lookup);
      const lookupResult = key ? databaseLookupMap.get(key) : null;

      if (lookupResult) {
        return {
          updated: true,
          field: {
            ...cf,
            resolvedValue: {
              value: lookupResult.value ?? '',
              units: null,
            },
            isSomeQuantityMissing: !lookupResult.found,
          },
        };
      }
    }
  }
  return { updated: false, field: cf };
}

/**
 * Applies a database lookup to an entry's main quantity if applicable.
 */
function applyLookupToQuantity(
  entry: ResolvedOrderEntry,
  databaseLookupMap: DatabaseLookupMap
): {
  updated: boolean;
  resolvedQuantity: ResolvedQuantity | null;
  isMissing: boolean;
} {
  if (
    entry.quantity?.type === QuantityEnum.DatabaseLookup &&
    entry.quantity.databaseLookup
  ) {
    const lookup = entry.quantity.databaseLookup;
    if (!isDynamicSearchTerm(lookup.searchTerm)) {
      const key = getDatabaseLookupKey(lookup);
      const lookupResult = key ? databaseLookupMap.get(key) : null;

      if (lookupResult) {
        return {
          updated: true,
          resolvedQuantity: {
            value: lookupResult.value ?? '',
            units: null,
          },
          isMissing: !lookupResult.found,
        };
      }
    }
  }
  return {
    updated: false,
    resolvedQuantity: entry.resolvedQuantity,
    isMissing: entry.isMissingResolvedQuantity,
  };
}

/**
 * Applies database lookup values to cached entries.
 * This ensures that even when entries are retrieved from cache,
 * they get updated with the latest database lookup results.
 */
async function applyDatabaseLookupsToCachedEntries(
  cachedEntries: Array<ResolvedOrderEntry>,
  orderColumns: OrderColumnDeepFragment[],
  databaseLookupMap: DatabaseLookupMap
): Promise<Array<ResolvedOrderEntry>> {
  const updatedEntries: Array<ResolvedOrderEntry> = [];

  for (const entry of cachedEntries) {
    let needsUpdate = false;
    const updatedEntry = { ...entry };

    const quantityResult = applyLookupToQuantity(entry, databaseLookupMap);
    if (quantityResult.updated) {
      updatedEntry.resolvedQuantity = quantityResult.resolvedQuantity;
      updatedEntry.isMissingResolvedQuantity = quantityResult.isMissing;
      needsUpdate = true;
    }

    // Check custom fields for database lookups
    if (entry.customFields && entry.customFields.length > 0) {
      const updatedCustomFields = entry.customFields.map((cf) => {
        const result = applyLookupToCustomField(
          cf,
          databaseLookupMap,
          entry.id
        );
        if (result.updated) {
          needsUpdate = true;
        }
        return result.field;
      });

      if (needsUpdate) {
        updatedEntry.customFields = updatedCustomFields;
      }
    }

    updatedEntries.push(needsUpdate ? updatedEntry : entry);
  }

  return updatedEntries;
}

function getEntriesInFrontendCache(
  entries: Array<Omit<OrderEntryDeepFragment, 'orderEntryCache'>>,
  isReadOnly: boolean
): [
  Array<ResolvedOrderEntry>,
  Array<Omit<OrderEntryDeepFragment, 'orderEntryCache'>>
] {
  // Attempt to retrieve the cache
  const resolvedEntries = [];
  const entriesNotInFrontendCache = [];
  for (const entry of entries) {
    const resolvedOrderEntry = orderEntriesCache.resolvedOrders.get(entry.id);
    // We can check if the we are in the shared order, if we are, we can push the result and continue with the next iteration
    if (isReadOnly && resolvedOrderEntry) {
      resolvedEntries.push(resolvedOrderEntry);
      continue;
    }

    const cachedEntryInput = orderEntriesCache.inputs.get(entry.id);
    // You can't use hashes here as the hash may be lagging the inputs
    const areInputsEqual = isEqual(entry, cachedEntryInput);
    if (resolvedOrderEntry && areInputsEqual) {
      resolvedEntries.push(resolvedOrderEntry);
      continue;
    }
    entriesNotInFrontendCache.push(entry);
  }
  return [resolvedEntries, entriesNotInFrontendCache];
}

function getEntriesInBackendCache(
  entries: Array<OrderEntryDeepFragment>,
  hashMap: Map<string, BulkHashOutput>,
  isReadOnly: boolean
): Array<ResolvedOrderEntry> {
  const retVal = [];
  for (const entry of entries) {
    // set the cache early and continue in the next iteration loop
    if (entry.orderEntryCache && isReadOnly) {
      retVal.push(entry.orderEntryCache.value);
      continue;
    }

    // will run if not in share mode
    const hashWithId = hashMap.get(entry.id);
    if (
      entry.orderEntryCache &&
      hashWithId &&
      hashWithId.hash === entry.orderEntryCache.hash
    ) {
      retVal.push(entry.orderEntryCache.value);
    }
  }
  return retVal;
}

// eslint-disable-next-line complexity
export async function resolveEntry(
  entry: Omit<OrderEntryDeepFragment, 'orderEntryCache'>,
  orderColumns: OrderColumnDeepFragment[],
  context: PropertyContext,
  projectSettings: {
    maxTrianglesForComplexAlgorithm: number;
    globalOffset: Point;
    unitSettings?: UnitSettingsType | undefined;
  },
  databaseLookupMap: DatabaseLookupMap = new Map(),
  dynamicLookupResolver?: DynamicLookupResolver
) {
  let resolvedElementIdentifiersDbid: ElementIdentifierDbidType[] = [];
  let resolvedQuantity: ResolvedQuantity | null = null;
  let resolvedCustomFields: ResolvedCustomField[] = [];
  let missingQuantity = false;

  const dynamicValues = await resolveOrderEntryDynamicValues(
    entry,
    orderColumns,
    context,
    projectSettings,
    databaseLookupMap,
    dynamicLookupResolver,
    entry.name // Pass entry name for dynamic search term evaluation
  );

  if (dynamicValues.resolvedElementIdentifiersDbid) {
    resolvedElementIdentifiersDbid =
      dynamicValues.resolvedElementIdentifiersDbid;
  }
  if (dynamicValues.resolvedQuantity) {
    resolvedQuantity = dynamicValues.resolvedQuantity;
  }
  if (dynamicValues.resolvedCustomFields) {
    resolvedCustomFields = dynamicValues.resolvedCustomFields;
  }
  if (dynamicValues.missingQuantity) {
    missingQuantity = dynamicValues.missingQuantity;
  }

  const resolvedEntry: ResolvedOrderEntry = {
    ...entry,
    resolvedQuantity: resolvedQuantity,
    resolvedElementIdentifiersDbid,
    customFields: resolvedCustomFields,
    isMissingResolvedQuantity: missingQuantity,
  };

  const subRows = await resolveSubRows(
    context,
    orderColumns,
    entry,
    resolvedElementIdentifiersDbid,
    entry.groupByAttributes?.[0] ?? null,
    entry.groupByAttributes?.slice(1) ?? null,
    projectSettings,
    databaseLookupMap,
    dynamicLookupResolver
  );

  resolvedEntry.subRows = subRows;

  return resolvedEntry;
}

// function getNumberOfBytes<T>(obj: T) {
//   const objStr = JSON.stringify(obj);
//   const sizeInBytes = new TextEncoder().encode(objStr).length;
//   return sizeInBytes / 1024 ** 2;
// }

export function getResolvedEntryForBackendCaching(
  resolvedOrderEntry: ResolvedOrderEntry
) {
  if (!resolvedOrderEntry) return resolvedOrderEntry;
  const resolvedQty = omit(resolvedOrderEntry.resolvedQuantity, 'dataPoints');
  const customFields = resolvedOrderEntry.customFields.map((field) => {
    const qty = omit(field.resolvedValue, 'dataPoints');
    return {
      ...field,
      resolvedValue: qty,
    };
  });

  const subRows: Array<ResolvedOrderEntry> =
    resolvedOrderEntry.subRows?.map(getResolvedEntryForBackendCaching) ?? [];

  return {
    ...resolvedOrderEntry,
    resolvedQuantity: resolvedQty,
    customFields,
    subRows,
  };
}

export type OrderEntryDynamicValues = {
  resolvedQuantity: ResolvedQuantity | null;
  missingQuantity: boolean;
  resolvedCustomFields: ResolvedCustomField[];
  resolvedElementIdentifiersDbid: ElementIdentifierDbidType[] | null;
};

export const resolveOrderEntryDynamicValues = async (
  orderEntry: Pick<
    OrderEntryDeepFragment,
    'elementIdentifiersQuery' | 'elementIdentifiersDbid' | 'customFields'
  >,
  orderColumns: Pick<OrderColumnDeepFragment, 'id' | 'columnType'>[],
  context: PropertyContext,
  projectSettings: {
    maxTrianglesForComplexAlgorithm: number;
    globalOffset: Point;
  },
  databaseLookupMap: DatabaseLookupMap = new Map(),
  dynamicLookupResolver?: DynamicLookupResolver,
  entryName?: string
): Promise<OrderEntryDynamicValues> => {
  let resolvedElementIdentifiersDbid = await resolveDbIds(
    context,
    orderEntry.elementIdentifiersQuery,
    orderEntry.elementIdentifiersDbid,
    projectSettings
  );
  const { resolvedCustomFields, resolvedQuantity, isMissingResolvedQuantity } =
    await resolveQuantities(
      orderEntry,
      orderColumns,
      resolvedElementIdentifiersDbid,
      context,
      projectSettings,
      databaseLookupMap,
      dynamicLookupResolver,
      entryName
    );
  return {
    resolvedQuantity,
    resolvedCustomFields,
    resolvedElementIdentifiersDbid,
    missingQuantity: isMissingResolvedQuantity,
  };
};

export async function resolveDbIds(
  context: PropertyContext,
  elementIdentifiersQuery: ForgeAttributeQueryType | null | undefined,
  elementIdentifiersDbid: ElementIdentifierDbidType[] | null | undefined,
  projectSettings: {
    maxTrianglesForComplexAlgorithm: number;
    globalOffset: Point;
    unitSettings?: UnitSettingsType;
  }
) {
  let result: ElementIdentifierDbidType[];

  if (elementIdentifiersQuery) {
    result = await resolveElementIdentifiersDBID(
      context,
      elementIdentifiersQuery,
      projectSettings
    );
  } else if (elementIdentifiersDbid) {
    result = excludeMissingDbIds(context, elementIdentifiersDbid);
  } else {
    return [];
  }

  // Filter out legend shapes from linking results
  return excludeLegendShapes(context, result);
}

// Exclude legend shapes from linked elements
// Legends are visual elements that should not be linked to order entries
function excludeLegendShapes(
  context: PropertyContext,
  elementIdentifiersDbid: ElementIdentifierDbidType[]
): ElementIdentifierDbidType[] {
  // Build a set of legend dbIds per urn from sheet shapes
  const legendDbIds: Record<string, Set<number>> = {};

  for (const [urn, shapes] of Object.entries(context.loadedSheetShapes)) {
    for (const shape of shapes) {
      if (shape.sheetShapeLegend) {
        if (!legendDbIds[urn]) {
          legendDbIds[urn] = new Set();
        }
        legendDbIds[urn].add(shape.dbId);
      }
    }
  }

  // Filter out legend dbIds
  return elementIdentifiersDbid
    .map((dbId) => {
      const legendSet = legendDbIds[dbId.modelUrn];
      if (legendSet) {
        return {
          ...dbId,
          dbIds: dbId.dbIds.filter((id) => !legendSet.has(id)),
        };
      }
      return dbId;
    })
    .filter((dbId) => dbId.dbIds.length > 0);
}

// Exclude links to models and shapes that are not present in the property context,
// Which can occur if for example a model is removed from the project, or if a shape is deleted
function excludeMissingDbIds(
  context: PropertyContext,
  elementIdentifiersDbid: ElementIdentifierDbidType[]
) {
  const loadedUrns = [
    ...Object.keys(context.loadedModels),
    ...Object.keys(context.loadedShapes),
    ...Object.keys(context.loadedSheetShapes),
  ];

  const loadedShapes = {
    ...context.loadedShapes,
    ...context.loadedSheetShapes,
  };

  return elementIdentifiersDbid
    .filter((dbId) => loadedUrns.includes(dbId.modelUrn))
    .map((dbId) => {
      if (dbId.modelUrn in loadedShapes) {
        const loadedShapeDbIds = loadedShapes[dbId.modelUrn].map(
          (shape) => shape.dbId
        );
        return {
          ...dbId,
          // Exclude shape dbIds that are not loaded, to handle deleted shapes
          dbIds: intersection(dbId.dbIds, loadedShapeDbIds),
        };
      }
      return dbId;
    });
}

export type Field = {
  columnId: string;
  quantity: Omit<Quantity, '__typename'>;
};

export type ResolvedField = {
  columnId: string;
  resolvedQuantity: ResolvedQuantity;
  isSomeQuantityMissing: boolean;
};

// eslint-disable-next-line complexity
async function resolveNonDerivedFields(
  nonDerivedFields: Field[],
  context: PropertyContext,
  resolvedElementIdentifiersDbid: ElementIdentifierDbidType[],
  projectSettings: {
    maxTrianglesForComplexAlgorithm: number;
    globalOffset: Point;
  },
  databaseLookupMap: DatabaseLookupMap = new Map(),
  dynamicLookupResolver?: DynamicLookupResolver,
  dynamicSearchTermScope?: DynamicSearchTermScope
): Promise<ResolvedField[]> {
  const result: ResolvedField[] = [];
  for (const field of nonDerivedFields) {
    // Handle DATABASE_LOOKUP
    if (
      field.quantity.type === QuantityEnum.DatabaseLookup &&
      field.quantity.databaseLookup
    ) {
      const lookup = field.quantity.databaseLookup;
      const searchTerm = lookup.searchTerm;

      // Check if this is a dynamic lookup
      if (
        isDynamicSearchTerm(searchTerm) &&
        dynamicLookupResolver &&
        dynamicSearchTermScope
      ) {
        // Evaluate the search term using the scope
        const evaluatedSearchTerm = evaluateDynamicSearchTerm(
          searchTerm ?? '',
          dynamicSearchTermScope
        );

        if (evaluatedSearchTerm) {
          // Query with the evaluated search term
          const lookupResult = await dynamicLookupResolver(
            lookup.databaseId ?? '',
            lookup.valueColumnId ?? '',
            lookup.searchColumnId ?? '',
            evaluatedSearchTerm,
            lookup.lookupProfileId ?? null
          );

          result.push({
            columnId: field.columnId,
            resolvedQuantity: {
              value: lookupResult.value ?? '',
              units: null,
            },
            isSomeQuantityMissing: !lookupResult.found,
          });
        } else {
          // Couldn't evaluate the search term
          result.push({
            columnId: field.columnId,
            resolvedQuantity: {
              value: '',
              units: null,
            },
            isSomeQuantityMissing: true,
          });
        }
      } else {
        // Static lookup - use pre-resolved map
        const key = getDatabaseLookupKey(lookup);
        const lookupResult = key
          ? databaseLookupMap.get(key)
          : { value: null, found: false };

        // Ensure value is never null (use empty string instead)
        const resolvedValue: string | number | (string | number)[] =
          lookupResult?.value ?? '';

        result.push({
          columnId: field.columnId,
          resolvedQuantity: {
            value: resolvedValue,
            units: null,
          },
          isSomeQuantityMissing: !lookupResult?.found,
        });
      }
      continue;
    }

    const resolvedQuantity = await resolveQuantity(
      context,
      resolvedElementIdentifiersDbid,
      field.quantity,
      projectSettings,
      undefined
    );
    const isMissing = isSomeQuantityMissing(
      resolvedQuantity,
      resolvedElementIdentifiersDbid
    );
    result.push({
      columnId: field.columnId,
      resolvedQuantity,
      isSomeQuantityMissing: isMissing,
    });
  }
  return result;
}

function getFields(
  orderEntry: Pick<
    OrderEntryDeepFragment,
    'customFields' | 'quantity' | 'unitPrice'
  >,
  orderColumns: Pick<OrderColumnDeepFragment, 'id' | 'columnType'>[],
  resolvedElementIdentifiersDbid: ElementIdentifierDbidType[]
): Field[] {
  const fields = orderEntry.customFields.map((cf) => ({
    columnId: cf.orderColumnId,
    quantity: cf.value,
  }));

  const quantityColumn = orderColumns.find(
    (col) => col.columnType === ColumnType.Quantity
  );

  if (quantityColumn && orderEntry.quantity) {
    fields.push({
      columnId: quantityColumn.id,
      quantity: orderEntry.quantity,
    });
  }

  const priceColumn = orderColumns.find(
    (col) => col.columnType === ColumnType.UnitPrice
  );

  if (priceColumn) {
    fields.push({
      columnId: priceColumn.id,
      quantity: {
        type: QuantityEnum.Static,
        value: orderEntry.unitPrice,
      },
    });
  }

  const elementsColumn = orderColumns.find(
    (col) => col.columnType === ColumnType.LinkedCount
  );

  if (elementsColumn) {
    fields.push({
      columnId: elementsColumn.id,
      quantity: {
        type: QuantityEnum.Static,
        value: resolvedElementIdentifiersDbid.length,
      },
    });
  }
  return fields;
}

function getResolvedQuantityFromFields(
  orderColumns: Pick<OrderColumnDeepFragment, 'id' | 'columnType'>[],
  resolvedFields: ResolvedField[]
): ResolvedField | null {
  const quantityColumn = orderColumns.find(
    (col) => col.columnType === ColumnType.Quantity
  );

  const resolvedQuantityField = resolvedFields.find(
    (field) => field.columnId === quantityColumn?.id
  );
  if (
    !resolvedQuantityField ||
    typeof resolvedQuantityField.resolvedQuantity.value !== 'number'
  ) {
    return null;
  }

  return resolvedQuantityField;
}

async function resolveQuantities(
  orderEntry: Pick<
    OrderEntryDeepFragment,
    'customFields' | 'quantity' | 'unitPrice'
  >,
  orderColumns: Pick<OrderColumnDeepFragment, 'id' | 'columnType'>[],
  resolvedElementIdentifiersDbid: ElementIdentifierDbidType[],
  context: PropertyContext,
  projectSettings: {
    maxTrianglesForComplexAlgorithm: number;
    globalOffset: Point;
  },
  databaseLookupMap: DatabaseLookupMap = new Map(),
  dynamicLookupResolver?: DynamicLookupResolver,
  entryName?: string
): Promise<ResolvedQuantities> {
  const fields = getFields(
    orderEntry,
    orderColumns,
    resolvedElementIdentifiersDbid
  );
  const [derivedFields, nonDerivedFields] = partition(
    fields,
    (cf) => cf.quantity.type === QuantityEnum.Derived
  );

  // Build scope for dynamic search term evaluation
  // We'll update this after resolving non-derived fields with column values
  const dynamicSearchTermScope: DynamicSearchTermScope = {
    name: entryName ?? '',
    columnValues: new Map(),
  };

  const resolvedNonDerivedFields = await resolveNonDerivedFields(
    nonDerivedFields,
    context,
    resolvedElementIdentifiersDbid,
    projectSettings,
    databaseLookupMap,
    dynamicLookupResolver,
    dynamicSearchTermScope
  );

  // Update scope with resolved column values for derived fields
  for (const field of resolvedNonDerivedFields) {
    dynamicSearchTermScope.columnValues.set(
      `var_${field.columnId.replaceAll('-', '_')}`,
      field.resolvedQuantity.value
    );
  }

  const resolvedDerivedFields = resolveDerivedFields(
    resolvedNonDerivedFields,
    derivedFields
  );
  const resolvedFields = [
    ...resolvedNonDerivedFields,
    ...resolvedDerivedFields,
  ];

  const resolvedQuantity = getResolvedQuantityFromFields(
    orderColumns,
    resolvedFields
  );

  const resolvedCustomFields = getResolvedCustomFieldsFromFields(
    orderColumns,
    resolvedFields,
    orderEntry
  );

  return {
    resolvedCustomFields,
    resolvedQuantity: resolvedQuantity?.resolvedQuantity ?? null,
    isMissingResolvedQuantity: resolvedQuantity?.isSomeQuantityMissing ?? false,
  };
}

function getResolvedCustomFieldsFromFields(
  orderColumns: Pick<OrderColumnDeepFragment, 'id' | 'columnType'>[],
  resolvedFields: ResolvedField[],
  orderEntry: Pick<
    OrderEntryDeepFragment,
    'customFields' | 'unitPrice' | 'quantity'
  >
) {
  const customColumnIds = orderColumns
    .filter((oc) => oc.columnType === ColumnType.Custom)
    .map((oc) => oc.id);
  return resolvedFields
    .filter((field) => customColumnIds.includes(field.columnId))
    .map((field): ResolvedCustomField => {
      const customField = orderEntry.customFields.find(
        (cf) => cf.orderColumnId === field.columnId
      );
      invariant(customField, 'Custom field not found');
      return {
        ...customField,
        resolvedValue: field.resolvedQuantity,
        isSomeQuantityMissing: field.isSomeQuantityMissing,
      };
    });
}

function isSomeQuantityMissing(
  resolvedQuantity: ResolvedQuantity | null,
  resolvedElementIdentifiersDbid: ElementIdentifierDbidType[] | undefined
) {
  if (!resolvedQuantity?.dataPoints || !resolvedElementIdentifiersDbid) {
    return false;
  }
  const inputDbids = resolvedElementIdentifiersDbid
    .flatMap((dbIds) => dbIds.dbIds.map((dbId) => `${dbIds.modelUrn}:${dbId}`))
    .sort();
  const outputDbids = resolvedQuantity.dataPoints
    .map((point) => `${point.modelUrn}:${point.dbId}`)
    .sort();
  return !isEqual(inputDbids, outputDbids);
}

export const resolveSubRows = async (
  context: PropertyContext,
  orderColumns: Pick<OrderColumnDeepFragment, 'id' | 'columnType'>[],
  entry: OrderEntryDeepFragment,
  dbIdsForParent: ElementIdentifierDbidType[],
  groupByAttribute: ForgeAttributeType | null,
  groupingChain: ForgeAttributeType[] | null,
  projectSettings: {
    maxTrianglesForComplexAlgorithm: number;
    globalOffset: Point;
    unitSettings?: UnitSettingsType;
  },
  databaseLookupMap: DatabaseLookupMap = new Map(),
  dynamicLookupResolver?: DynamicLookupResolver
): Promise<ResolvedOrderEntry[] | undefined> => {
  if (!context || groupByAttribute === null || !dbIdsForParent) {
    return undefined;
  }

  // Early return if there are no elements to group - no subrows possible
  const totalDbIds = dbIdsForParent.reduce(
    (acc, id) => acc + id.dbIds.length,
    0
  );
  if (totalDbIds === 0) {
    return [];
  }

  const subRows = await findSubrows(
    context,
    dbIdsForParent,
    groupByAttribute,
    entry.elementIdentifiersQuery,
    projectSettings
  );

  const { sortedSubRows, minValue, maxValue } =
    sortSubrowsAndExtractMaxima(subRows);

  let currentIndex = 0;

  const resolveSubRowEntries: ResolvedOrderEntry[] = [];
  for (const {
    subrowName,
    dbIdsForSubrow,
    elementIdentifiersQuery,
  } of sortedSubRows) {
    const {
      resolvedCustomFields,
      resolvedQuantity,
      isMissingResolvedQuantity,
    } = await resolveQuantities(
      entry,
      orderColumns,
      dbIdsForSubrow,
      context,
      projectSettings,
      databaseLookupMap,
      dynamicLookupResolver,
      subrowName // Use subrow name for dynamic search term evaluation
    );

    const color = getSubrowColor(
      entry,
      sortedSubRows,
      currentIndex,
      minValue,
      maxValue
    );
    const entryWithoutCache = omit(entry, ['orderEntryCache']);

    const resolvedEntry: ResolvedOrderEntry = {
      ...entryWithoutCache,
      color,
      name: subrowName,
      isMissingResolvedQuantity,
      elementIdentifiersQuery,
      groupByAttributes: null,
      resolvedQuantity: resolvedQuantity,
      resolvedElementIdentifiersDbid: dbIdsForSubrow,
      customFields: resolvedCustomFields,
    };

    if (groupingChain !== null && groupingChain.length > 0) {
      resolvedEntry.subRows = await resolveSubRows(
        context,
        orderColumns,
        { ...entryWithoutCache, color },
        dbIdsForSubrow,
        groupingChain[0],
        groupingChain.slice(1),
        projectSettings,
        databaseLookupMap,
        dynamicLookupResolver
      );
    }
    resolveSubRowEntries.push(resolvedEntry);

    currentIndex++;
  }

  return resolveSubRowEntries;
};

function sortSubrowsAndExtractMaxima(
  subRows: {
    subrowName: string;
    dbIdsForSubrow: ElementIdentifierDbidType[];
    elementIdentifiersQuery: ForgeAttributeQueryType | null;
  }[]
) {
  // Determine if subrowName values are numeric
  const areSubrowNamesNumeric = subRows.every(
    (row) => !isNaN(Number(row.subrowName))
  );
  let maxValue = undefined;
  let minValue = undefined;

  let sortedSubRows = subRows;

  if (areSubrowNamesNumeric) {
    // Find the max and min values
    for (const row of sortedSubRows) {
      const value = Number(row.subrowName);
      if (!maxValue || value > maxValue) {
        maxValue = value;
      }
      if (!minValue || value < minValue) {
        minValue = value;
      }
    }

    // Sort the subrows by the numeric value
    sortedSubRows = subRows.sort(
      (a, b) => Number(a.subrowName) - Number(b.subrowName)
    );
  } else {
    // Sort the subrows by the subrowName
    sortedSubRows = subRows.sort((a, b) =>
      a.subrowName.localeCompare(b.subrowName)
    );
  }

  return {
    sortedSubRows,
    minValue,
    maxValue,
  };
}

function getSubrowColor(
  entry: OrderEntryDeepFragment,
  allSubRows: {
    subrowName: string;
    dbIdsForSubrow: ElementIdentifierDbidType[];
    elementIdentifiersQuery: ForgeAttributeQueryType | null;
  }[],
  index: number,
  minValue: number | undefined,
  maxValue: number | undefined
) {
  // Decode the parent color to extract any pattern encoding
  const { color: baseColor, pattern } = decodeColorWithPattern(entry.color);

  let color = baseColor;

  if (color.includes('gradient')) {
    let factor;
    if (allSubRows.length === 1) {
      factor = 0.5;
    } else {
      factor = index / (allSubRows.length - 1);
    }

    if (
      !isNaN(Number(entry.name)) &&
      maxValue &&
      minValue &&
      maxValue !== minValue
    ) {
      // Calculate the factor based on the min and max values
      factor = (Number(entry.name) - minValue) / (maxValue - minValue);
    }
    color = getColorValueFromGradient(color, factor);
  }

  // Re-encode with the pattern from the parent (if any)
  return encodeColorWithPattern(color, pattern);
}

async function findSubrows(
  context: PropertyContext,
  dbIds: ElementIdentifierDbidType[],
  groupByAttribute: ForgeAttributeType,
  dbIdQuery: ForgeAttributeQueryType | null | undefined,
  projectSettings: {
    maxTrianglesForComplexAlgorithm: number;
    globalOffset: Point;
  },
  unitSettings?: UnitSettingsType
) {
  const properties = (
    await resolveProperties(context, projectSettings, groupByAttribute, dbIds)
  ).map((property) => {
    if (typeof property.value === 'number') {
      // Round for grouping by floating point numbers
      if (property.units && unitSettings) {
        const propertyAsSIQuantity = convertQuantityToSIUnits(property);
        const convertedValue = convertSIQuantity(
          {
            value: propertyAsSIQuantity.value,
            units: propertyAsSIQuantity.units,
          },
          unitSettings
        );

        if (!convertedValue || !convertedValue.value || !convertedValue.units) {
          return property;
        }

        const unitType = unitToUnitType(convertedValue.units);
        const rounding = unitType
          ? unitSettings[unitType]?.rounding
          : undefined;
        const precision = rounding ? roundingToPrecision(rounding) : 2;

        const propertyValue = round(convertedValue.value as number, precision);
        const propertyUnits = formatUnit(convertedValue.units);

        property.value = `${propertyValue} ${propertyUnits}`;
      } else {
        property.value = round(
          convertQuantityToSIUnits(property).value as number,
          2
        );
      }
    }
    return property;
  });

  const propertiesForSubrows = groupBy(
    properties,
    (property) => property.value
  );

  const dbIdsForSubrows = Object.entries(propertiesForSubrows).map(
    ([subrowName, propertiesForSubrow]) => {
      const propertiesForModel = groupBy(
        propertiesForSubrow,
        (property) => property.modelUrn
      );
      const dbIdsForSubrow: ElementIdentifierDbidType[] = Object.entries(
        propertiesForModel
      ).map(([modelUrn, properties]) => ({
        modelUrn,
        dbIds: properties.map((properties) => properties.dbId),
      }));
      let newDbIdQuery: ForgeAttributeQueryType | null = null;
      if (dbIdQuery) {
        const predicate: ForgeAttributePredicateType = {
          attribute: {
            category: groupByAttribute.category,
            name: groupByAttribute.name,
          },
          predicate: PredicateType.Equals,
          attributeValues: [subrowName],
        };
        newDbIdQuery = {
          predicates: [...dbIdQuery.predicates, predicate],
        };
      }
      return {
        subrowName,
        dbIdsForSubrow,
        elementIdentifiersQuery: newDbIdQuery,
      };
    }
  );

  const dbIdsMissingGroupByAttribute = difference(
    dbIds,
    dbIdsForSubrows
      .map((dbIds) => dbIds.dbIdsForSubrow)
      .reduce((prev, next) => merge(prev, next), [])
  );

  if (!isEmpty(dbIdsMissingGroupByAttribute)) {
    let subrowDbIdQuery: ForgeAttributeQueryType | null = null;
    if (dbIdQuery) {
      const predicate: ForgeAttributePredicateType = {
        attribute: {
          category: groupByAttribute.category,
          name: groupByAttribute.name,
        },
        predicate: PredicateType.NotExists,
        attributeValues: [],
      };
      subrowDbIdQuery = {
        predicates: [...dbIdQuery.predicates, predicate],
      };
    }
    dbIdsForSubrows.push({
      subrowName: `Missing property ${groupByAttribute.name}`,
      dbIdsForSubrow: dbIdsMissingGroupByAttribute,
      elementIdentifiersQuery: subrowDbIdQuery,
    });
  }
  return dbIdsForSubrows;
}
